import {
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { Interaction } from '@/types';

interface InteractionPromptProps {
  interaction: Interaction;
  onSubmit: (
    requestId: string,
    inputs: Record<string, string>
  ) => Promise<void>;
}

interface Field {
  name: string;
  value: string;
}

// Renders a pending agent credential request: the agent's prompt plus a dynamic
// name/value form. The operator types the values the agent will reference as
// {{name}} placeholders; on submit they're stashed as an ephemeral secret and
// the interaction is answered. Values never leave as plaintext past the broker.
export function InteractionPrompt({
  interaction,
  onSubmit,
}: InteractionPromptProps) {
  // The agent declares the fields it wants (its {{name}} placeholders). Fall
  // back to a single generic field if it asked for none.
  const requested =
    interaction.fields && interaction.fields.length > 0
      ? interaction.fields
      : ['input'];
  const [fields, setFields] = useState<Field[]>(
    requested.map(name => ({ name, value: '' }))
  );
  const [revealed, setRevealed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // The reveal toggle only masks typed text, so hide it until there's something
  // to reveal — otherwise clicking it on empty fields looks like a no-op.
  const hasValue = fields.some(f => f.value !== '');

  const setField = (idx: number, patch: Partial<Field>) =>
    setFields(prev => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const addField = () => setFields(prev => [...prev, { name: '', value: '' }]);
  const removeField = (idx: number) =>
    setFields(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    const inputs: Record<string, string> = {};
    for (const f of fields) {
      const name = f.name.trim();
      if (name && f.value) inputs[name] = f.value;
    }
    if (Object.keys(inputs).length === 0) {
      toast.error('Enter at least one named credential field');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(interaction.requestId, inputs);
    } catch (err) {
      console.error('Failed to submit credentials', err);
      toast.error('Failed to send credentials to the agent');
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (!submitting) void handleSubmit();
      }}
      className="border-chariot-brand-primary/40 bg-chariot-background-primary mx-6 mb-3 overflow-hidden rounded-xl border shadow-sm"
    >
      <div className="border-chariot-stroke flex items-center gap-2.5 border-b px-4 py-3">
        <div className="bg-chariot-brand-primary/10 text-chariot-brand-primary flex size-7 shrink-0 items-center justify-center rounded-full">
          <KeyIcon className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-chariot-text-primary text-sm font-semibold">
            Credentials requested
          </p>
          <p className="text-chariot-text-secondary truncate text-xs">
            Sent securely to the agent for this session only — not persisted.
          </p>
        </div>
        {hasValue && (
          <button
            type="button"
            onClick={() => setRevealed(v => !v)}
            className="text-chariot-text-secondary hover:text-chariot-text-primary shrink-0 transition-colors"
            title={revealed ? 'Hide values' : 'Show values'}
            aria-label={revealed ? 'Hide values' : 'Show values'}
          >
            {revealed ? (
              <EyeIcon className="size-4" />
            ) : (
              <EyeSlashIcon className="size-4" />
            )}
          </button>
        )}
      </div>

      <div className="px-4 py-3">
        <p className="text-chariot-text-secondary mb-3 text-sm leading-relaxed">
          {interaction.request}
        </p>

        <div className="flex flex-col gap-2">
          {fields.map((f, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input
                value={f.name}
                placeholder="field"
                disabled={submitting}
                onChange={e => setField(idx, { name: e.target.value })}
                className="text-chariot-text-secondary w-32 shrink-0 font-mono text-xs"
              />
              <Input
                type={revealed ? 'text' : 'password'}
                value={f.value}
                placeholder="value"
                autoComplete="off"
                disabled={submitting}
                onChange={e => setField(idx, { value: e.target.value })}
                className="flex-1"
              />
              <button
                type="button"
                aria-label="Remove field"
                disabled={submitting || fields.length === 1}
                onClick={() => removeField(idx)}
                className={cn(
                  'text-chariot-text-secondary hover:text-chariot-text-primary shrink-0 transition-colors',
                  fields.length === 1 && 'invisible'
                )}
              >
                <XMarkIcon className="size-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between">
          <button
            type="button"
            disabled={submitting}
            onClick={addField}
            className="text-chariot-brand-primary hover:text-chariot-brand-primary/80 flex items-center gap-1 text-sm font-medium transition-colors disabled:opacity-50"
          >
            <PlusIcon className="size-4" /> Add field
          </button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send to agent'}
          </Button>
        </div>
      </div>
    </form>
  );
}
